import { useCallback, useContext, useMemo, useState } from 'react';
import type { AssistantEventType, AssistantInformation } from '~/api/aspida/@types';
import { getAssistant } from '~/api/assistant';
import { useUser } from '~/api/hooks/useGetAccount';
import { ModalContext } from '~/context/ModalContext';
import { AssistantModal } from '~/features/assistant/components/AssistantModal';
import { useForecastGraph } from '~/features/record/hooks/useForecastGraph';
import { generateRecordRoute } from '~/features/record/lib/generateRecordRoute';
import { useRequestApi } from '~/hooks/useRequestApi';
import type { UnixTime } from '~/interfaces/common';
import { assistantPersistences } from '~/persistence';
import { getCurrentSeconds } from '~/utils/time';

type ClientEventId =
  | 'client_visit_home_from_measure'
  | 'client_visit_home_from_cloakroom'
  | 'client_swipe_record_growth'
  | 'client_visit_record_growth';

export type AssistantEventId = AssistantEventType | ClientEventId;

export type AssistantIntervalItem = {
  asics_id?: string;
  child_id?: string;
  event_id: AssistantEventId;
  interval: UnixTime;
};

type AssistantEvent = Omit<AssistantInformation, 'event_type'> & {
  event_type: AssistantEventId;
};

const CLIENT_ASSISTANT_EVENTS: AssistantEvent[] = [
  {
    need_recommended: false,
    display: 'home',
    event_type: 'client_visit_home_from_measure',
    interval: 1,
    message: 'assistant_visit_home_from_measure',
    priority: 4,
    popup_size: 'small',
  },
  {
    need_recommended: false,
    display: 'home',
    event_type: 'client_visit_home_from_cloakroom',
    interval: 1,
    message: 'assistant_visit_home_from_cloakroom',
    priority: 3,
    popup_size: 'small',
  },
  {
    need_recommended: false,
    display: 'graph',
    event_type: 'client_swipe_record_growth',
    interval: 1,
    message: 'assistant_swipe_record_growth',
    priority: 12,
    popup_size: 'small',
    destination: generateRecordRoute({ tabKey: 'shoes' }),
  },
  {
    need_recommended: false,
    display: 'graph',
    event_type: 'client_visit_record_growth',
    interval: 1,
    message: 'assistant_visit_record_growth',
    priority: 18,
    popup_size: 'small',
    destination: '/record',
  },
];

type PopoverItem = Pick<AssistantEvent, 'destination' | 'message' | 'need_recommended'> & {
  eventId: AssistantEventId;
};

type AssistantDisplay = 'graph' | 'home';

export const useAssistant = (display: AssistantDisplay) => {
  const { openModal } = useContext(ModalContext);
  const assistantIntervalItems = useMemo(
    () =>
      (Array.isArray(assistantPersistences.Interval.value) &&
        assistantPersistences.Interval.value) ||
      [],
    [],
  );
  const { data: user } = useUser();
  const { data: growth } = useForecastGraph();
  const [assistants, setAssistants] = useState<AssistantEvent[]>([]);
  const [showBirthday, setShowBirthday] = useState(false);
  const [showPromising, setShowPromising] = useState(false);
  const [shownAssistants, setShownAssistants] = useState(false);

  const addClientAssistantById = useCallback((eventId: AssistantEventId) => {
    const event = CLIENT_ASSISTANT_EVENTS.find(
      (clientAssistantEvent) => clientAssistantEvent.event_type === eventId,
    );
    if (!event) return;
    // 重複チェックも setState 内で行うことで stale closure を避け、
    // useCallback の依存配列を空にして関数参照を安定化させる
    setAssistants((oldAssistants) => {
      const existsEvent = oldAssistants.find(
        (assistant) => assistant.event_type === event.event_type,
      );
      if (existsEvent) return oldAssistants;
      return [...oldAssistants, event].sort(
        (firstAssistant, secondAssistant) => firstAssistant.priority - secondAssistant.priority,
      );
    });
  }, []);

  const getAssistantRequest = useRequestApi(getAssistant, (res) => {
    const { informations } = res;
    setAssistants((oldAssistants) =>
      [...oldAssistants, ...informations].sort(
        (firstAssistant, secondAssistant) => firstAssistant.priority - secondAssistant.priority,
      ),
    );
  });

  const availableAssistants = useMemo(() => {
    // user ロード前はアシスタント抑止
    if (!user) return [];
    return assistants.filter((assistant) => {
      const intervalItems = assistantIntervalItems.filter((item) => {
        if (['retention', 'shoe_registration'].includes(item.event_id)) {
          return item.asics_id === String(user.asics_id);
        }
        return item.child_id === String(user.selected_child_id);
      });
      const interval =
        intervalItems.find((item) => item.event_id === assistant.event_type)?.interval ?? 0;
      return interval < getCurrentSeconds() && assistant.display === display;
    });
  }, [assistantIntervalItems, assistants, display, user]);

  const showAssistantModals = useCallback(() => {
    if (shownAssistants) return;
    const largeAssistants = availableAssistants.filter(
      (assistant) => assistant.popup_size === 'large',
    );
    for (const assistant of largeAssistants) {
      const { event_type, destination, message, image, need_recommended } = assistant;
      openModal(() =>
        AssistantModal({
          eventId: event_type,
          destination,
          need_recommended,
          message,
          imageUrl: image,
        }),
      );
    }
    if (largeAssistants.length > 0) setShownAssistants(true);
  }, [availableAssistants, openModal, shownAssistants]);

  const popoverItems = useMemo(() => {
    const showFromMeasure =
      growth &&
      growth.graph_data.filter((data) => data.left_length && data.right_length).length >= 2;
    return availableAssistants.reduce((prevItems: PopoverItem[], currentEvent) => {
      if (
        currentEvent.popup_size === 'small' &&
        ((currentEvent.event_type !== 'birthday' &&
          currentEvent.event_type !== 'promising' &&
          currentEvent.event_type !== 'client_visit_home_from_measure') ||
          (currentEvent.event_type === 'birthday' && showBirthday) ||
          (currentEvent.event_type === 'promising' && showPromising) ||
          (currentEvent.event_type === 'client_visit_home_from_measure' && showFromMeasure))
      ) {
        const { event_type, destination, message, need_recommended } = currentEvent;
        const popoverItem = { eventId: event_type, destination, message, need_recommended };
        prevItems.push(popoverItem);
      }
      return prevItems;
    }, []);
  }, [availableAssistants, growth, showBirthday, showPromising]);

  return {
    getAssistantRequest,
    setShowBirthday,
    setShowPromising,
    addClientAssistantById,
    showAssistantModals,
    popoverItems,
  };
};
